import { Injectable, inject } from '@angular/core';
import { AppActions } from '@navfleet/client/core/app';
import { LicenseFacade } from '@navfleet/client/license/domain/data-access';
import { License } from '@navfleet/client/license/domain/models';
import {
    VesselPassageActions,
    VesselPassageApi,
    VesselPassageSelectors,
    fromVesselPassage,
} from '@navfleet/client/monitoring/state/vessel-passage';
import { VesselPassagePlanActions } from '@navfleet/client/monitoring/state/vessel-passage-plan';
import { VesselRouteActions } from '@navfleet/client/monitoring/state/vessel-route';
import { VesselScheduleActions } from '@navfleet/client/monitoring/state/vessel-schedule';
import { logDebug, logError } from '@navfleet/client/shared/util/log';
import {
    VesselMethod,
    VesselSelectors,
    VesselSignalRService,
    fromVessel,
} from '@navfleet/client/vessel/domain/data-access';
import { VesselPassageSignalRData, VesselPassageState } from '@navfleet/client/vessel/domain/models';
import {
    loadAllActivePassagesSuccess,
    loadPassagesWithDetailsSuccess,
} from '@navfleet/client/vessel/passage/domain/models';
import { equal } from '@navfleet/shared/utils';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store, select } from '@ngrx/store';
import { EMPTY, Observable, combineLatest, merge, of } from 'rxjs';
import {
    catchError,
    concatMap,
    distinctUntilChanged,
    filter,
    map,
    mergeMap,
    shareReplay,
    switchMap,
    take,
    takeUntil,
    tap,
    withLatestFrom,
} from 'rxjs/operators';

@Injectable()
export class VesselPassageEffects {
    private readonly actions$ = inject(Actions);
    private readonly store$ = inject<Store<fromVesselPassage.State & fromVessel.State>>(Store);
    private readonly vesselPassageApi = inject(VesselPassageApi);
    private readonly vesselSignalRService = inject(VesselSignalRService);
    private readonly licenseFacade = inject(LicenseFacade);

    private readonly hasPassageLicenses$: Observable<[boolean, boolean]> = combineLatest([
        this.licenseFacade.hasLicense(License.PassageDisplayActive),
        this.licenseFacade.hasLicense(License.PassageMonitoring),
    ]);

    public readonly initLoadOfVesselPassages$ = createEffect(() =>
        this.actions$.pipe(
            ofType(VesselPassageActions.initLoadOfVesselPassages),
            withLatestFrom(this.hasPassageLicenses$),
            filter(([_, licenses]) => licenses.some((license) => !!license)),
            switchMap(
                ([
                    { force, loadVesselSchedule, vesselId, vesselPassageId, vesselPassageState, loadGeometry },
                    [_, hasMonitoringLicense],
                ]) => {
                    if (!hasMonitoringLicense && vesselPassageState !== VesselPassageState.Active) {
                        return EMPTY;
                    }
                    const args = {
                        loadVesselSchedule: !!loadVesselSchedule,
                        vesselId: vesselId as number,
                        vesselPassageId: vesselPassageId as number,
                        vesselPassageState: vesselPassageState as VesselPassageState,
                        loadGeometry: loadGeometry as boolean,
                    };
                    if (force) {
                        return of(
                            VesselPassageActions.loadVesselPassages({
                                force,
                                args,
                            })
                        );
                    }

                    return this.store$.pipe(
                        select(VesselPassageSelectors.selectIsVesselPassagesLoadingOrLoaded(args)),
                        withLatestFrom(
                            this.store$.select(
                                VesselPassageSelectors.selectVesselPassageError({
                                    vesselId,
                                    vesselPassageId: vesselPassageId as number,
                                    vesselPassageState,
                                })
                            )
                        ),
                        take(1),
                        concatMap(([vesselPassageIsLoadedOrLoading, error]) => {
                            // Return empty if vessel passage is loading or loaded
                            if (vesselPassageIsLoadedOrLoading) {
                                return EMPTY;
                            }

                            if (error) {
                                logError('VesselPassageEffects', 'initLoadOfVesselPassages$', 'error', error);
                                return EMPTY;
                            }

                            return of(
                                VesselPassageActions.loadVesselPassages({
                                    args,
                                })
                            );
                        }),
                        takeUntil(this.actions$.pipe(ofType(AppActions.applicationClosed)))
                    );
                }
            )
        )
    );

    public readonly loadVesselPassages$ = createEffect(() =>
        this.actions$.pipe(
            ofType(VesselPassageActions.loadVesselPassages),
            withLatestFrom(this.hasPassageLicenses$),
            filter(([_, licenses]) => licenses.some((license) => !!license)),
            mergeMap(([{ force, args }, [_, hasMonitoringLicense]]) => {
                const { loadVesselSchedule, vesselId, vesselPassageId, vesselPassageState, loadGeometry } = args;
                // Return EMPTY if the user only has license to active passages and the arguments are not for loading active
                if (!hasMonitoringLicense && vesselPassageState !== VesselPassageState.Active) {
                    return EMPTY;
                }

                return this.vesselPassageApi.getVesselPassages(vesselId, vesselPassageState, vesselPassageId).pipe(
                    take(1),
                    concatMap((vesselPassages) => {
                        // Array with actions to load related data
                        const loadDataActions: Action[] = [];

                        vesselPassages.forEach((passage) => {
                            if (hasMonitoringLicense || passage.passageState === VesselPassageState.Active) {
                                const shouldLoadSchedule =
                                    hasMonitoringLicense &&
                                    loadVesselSchedule &&
                                    passage.passageState === VesselPassageState.Active;

                                if (passage.passagePlanId) {
                                    loadDataActions.push(
                                        VesselPassagePlanActions.initLoadOfVesselPassagePlans({
                                            force,
                                            passagePlanId: passage.passagePlanId,
                                            // Only load geometry if there is only one passage
                                            loadGeometry: loadGeometry || vesselPassages.length === 1,
                                        })
                                    );
                                }

                                if (passage.routeId) {
                                    loadDataActions.push(
                                        VesselRouteActions.initLoadOfVesselRoutes({
                                            force,
                                            // Passing down the loadVesselSchedule for further validation of the route
                                            loadVesselSchedule,
                                            vesselRouteId: passage.routeId,
                                            // Only load geometry if there is only one passage
                                            loadGeometry: loadGeometry || vesselPassages.length === 1,
                                        })
                                    );
                                }

                                if (shouldLoadSchedule) {
                                    // Added load of schedule here since there is no need to check the passage plan
                                    loadDataActions.push(
                                        VesselScheduleActions.initLoadOfVesselSchedules({
                                            force,
                                            vesselId: passage.vesselId,
                                            vesselPassageHaveBeenVerified: true,
                                        })
                                    );
                                }
                            }
                        });

                        return [
                            ...loadDataActions,
                            VesselPassageActions.loadVesselPassagesSuccess({
                                vesselPassages,
                                args,
                            }),
                        ];
                    }),
                    catchError((error) =>
                        of(
                            VesselPassageActions.loadVesselPassagesFailure({
                                error,
                                args,
                            })
                        )
                    ),
                    takeUntil(this.actions$.pipe(ofType(AppActions.applicationClosed)))
                );
            })
        )
    );

    public readonly loadAllActivePassages$ = createEffect(() =>
        this.actions$.pipe(
            ofType(VesselPassageActions.loadAllActivePassages),
            withLatestFrom(this.hasPassageLicenses$),
            filter(([_, licenses]) => licenses.some((license) => !!license)),
            mergeMap(() =>
                this.vesselPassageApi.getActivePassages().pipe(
                    map((responses) => loadAllActivePassagesSuccess({ vessels: responses })),
                    catchError((error) => of(VesselPassageActions.loadAllActivePassagesFailure({ error })))
                )
            )
        )
    );

    /**
     * Reloads a passage with its plan, route, schedule and events in one request.
     * Per passage only the newest reload is applied, so an older response cannot overwrite newer data.
     */
    public readonly loadPassageWithDetails$ = createEffect(() =>
        this.actions$.pipe(
            ofType(VesselPassageActions.loadPassageWithDetails),
            // Passages in any state with their details are for the passage monitoring license only
            withLatestFrom(this.hasPassageLicenses$),
            filter(([, [, hasMonitoringLicense]]) => hasMonitoringLicense),
            mergeMap(([{ vesselId, passageId, passageState }]) =>
                this.vesselPassageApi.getPassageWithDetails(vesselId, passageId, passageState).pipe(
                    // A newer load of the same passage replaces this one, and a response after the app was closed
                    // must not fill the reset store again
                    takeUntil(
                        merge(
                            this.actions$.pipe(
                                ofType(VesselPassageActions.loadPassageWithDetails),
                                filter((newerLoad) => newerLoad.passageId === passageId)
                            ),
                            this.actions$.pipe(ofType(AppActions.applicationClosed))
                        )
                    ),
                    map((vessels) => loadPassagesWithDetailsSuccess({ vessels })),
                    catchError((error) => {
                        logError('VesselPassageEffects', 'loadPassageWithDetails$', passageId, error);
                        return EMPTY;
                    })
                )
            )
        )
    );

    public readonly listenToVesselPassageAdded$ = createEffect(
        () =>
            this.hasPassageLicenses$.pipe(
                switchMap(([hasDisplayActiveLicense, hasMonitoringLicense]) =>
                    !hasDisplayActiveLicense && !hasMonitoringLicense
                        ? EMPTY
                        : this.vesselSignalRService.on$(VesselMethod.PassageAdded).pipe(
                              tap((event) => this.handleSignalR(VesselMethod.PassageAdded, event, hasMonitoringLicense))
                          )
                )
            ),

        { dispatch: false }
    );

    public readonly listenToVesselPassageUpdated$ = createEffect(
        () =>
            this.hasPassageLicenses$.pipe(
                switchMap(([hasDisplayActiveLicense, hasMonitoringLicense]) =>
                    !hasDisplayActiveLicense && !hasMonitoringLicense
                        ? EMPTY
                        : this.vesselSignalRService.on$(VesselMethod.PassageUpdated).pipe(
                              tap((event) =>
                                  this.handleSignalR(VesselMethod.PassageUpdated, event, hasMonitoringLicense)
                              )
                          )
                )
            ),

        { dispatch: false }
    );

    private vesselIds$: Observable<number[]> = this.store$.select(VesselSelectors.selectNavFleetVessels).pipe(
        filter((vessels) => !!vessels.length),
        map((vessels) => vessels.map((v) => v.id)),
        distinctUntilChanged(equal),
        shareReplay({ bufferSize: 1, refCount: true })
    );

    private handleSignalR(
        signalrMethod: VesselMethod,
        vesselPassageData: VesselPassageSignalRData,
        hasMonitoringLicense: boolean
    ): void {
        this.vesselIds$.pipe(take(1)).subscribe({
            next: (vesselIds) => {
                const vesselId = vesselPassageData.vesselId;
                const hasVessel = vesselIds.includes(vesselId);

                logDebug(
                    'SignalR',
                    'VesselPassageEffects',
                    `${signalrMethod} received for vesselId ${vesselId}`,
                    'Passage matches vesselId',
                    hasVessel,
                    'VesselPassageData:',
                    vesselPassageData
                );

                if (!hasVessel) {
                    return;
                }

                const vesselPassage = vesselPassageData.convertToVesselPassage();

                const shouldUpdatePassage =
                    hasMonitoringLicense || vesselPassage.passageState === VesselPassageState.Active;

                if (shouldUpdatePassage) {
                    // Dispatch to add new data to store
                    this.store$.dispatch(
                        VesselPassageActions.signalRVesselPassageUpdatedSuccess({
                            vesselPassage,
                        })
                    );
                }

                if (hasMonitoringLicense) {
                    // One request gives the passage with its plan, route, schedule and events, which SignalR does
                    // not send. Editing a passage stores each changed field as an event that does not come through
                    // EventAdded, and the geometry of a route can change at any time.
                    this.store$.dispatch(
                        VesselPassageActions.loadPassageWithDetails({
                            vesselId,
                            passageId: vesselPassage.id,
                            passageState: vesselPassage.passageState,
                        })
                    );
                }

                // Clear VesselSchedule when Passage has been deactivated or cancelled (no longer active)
                if (
                    signalrMethod === VesselMethod.PassageUpdated &&
                    vesselPassage.passageState !== VesselPassageState.Active
                ) {
                    this.store$.dispatch(
                        VesselScheduleActions.clearVesselSchedule({
                            vesselId: vesselPassageData.vesselId,
                        })
                    );
                }
            },
        });
    }
}
